import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAutoClearState } from "../../../../common/hooks/useAutoClearState";
import { getChatDeviceSetting, saveChatDeviceSetting } from "../../apis/chatApi";
import BackHeader from "../shared/BackHeader/BackHeader";
import Toast from "../shared/Toast/Toast";
import styles from "./ChatSettingsPage.module.scss";
import settingsListStyles from "./SettingsList/SettingsList.module.scss";
import SettingsList from "./SettingsList/SettingsList";
import TimeDropdown from "./TimeDropdown/TimeDropdown";

function formatTimeHint(from, to) {
  const format = (t) => {
    const [h, m] = t.split(":").map(Number);
    const suffix = h < 12 ? "오전" : "오후";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return m === 0 ? `${suffix} ${h12}시` : `${suffix} ${h12}시 ${m}분`;
  };
  return `${format(from)}부터 ${format(to)}까지 알림을 받지 않습니다.`;
}

function ChatSettingsPage({
  className = "",
  title = "",
  onBackClick,
  onOpenBlockedUsers,
  toastMessage,
}) {
  const [notificationOn, setNotificationOn] = useState(true);
  const [dndOn, setDndOn] = useState(false);
  const [dndFrom, setDndFrom] = useState("22:00");
  const [dndTo, setDndTo] = useState("08:00");
  const [settingToast, setSettingToast] = useAutoClearState(null, 3000);
  const [loaded, setLoaded] = useState(false);

  // 유저 설정 로드
  useEffect(() => {
    getChatDeviceSetting().then((data) => {
      setNotificationOn(data.chat_noti_enabled !== 0);
      setDndOn(data.dnd_enabled !== 0);
      if (data.dnd_from) setDndFrom(data.dnd_from);
      if (data.dnd_to) setDndTo(data.dnd_to);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const saveSettings = useCallback((overrides) => {
    saveChatDeviceSetting(overrides);
  }, []);

  const handleNotificationToggle = useCallback((val) => {
    setNotificationOn(val);
    setSettingToast(val ? "채팅 알림을 켰습니다." : "채팅 알림을 껐습니다.");
    saveSettings({ chat_noti_enabled: val ? 1 : 0, dnd_enabled: dndOn ? 1 : 0, dnd_from: dndFrom, dnd_to: dndTo });
  }, [dndOn, dndFrom, dndTo, saveSettings, setSettingToast]);

  const handleDndToggle = useCallback((val) => {
    setDndOn(val);
    setSettingToast(val ? "방해금지 모드를 활성화 했습니다." : "방해금지 모드를 비활성화 했습니다.");
    saveSettings({ chat_noti_enabled: notificationOn ? 1 : 0, dnd_enabled: val ? 1 : 0, dnd_from: dndFrom, dnd_to: dndTo });
  }, [notificationOn, dndFrom, dndTo, saveSettings, setSettingToast]);

  const handleDndFromChange = useCallback((val) => {
    setDndFrom(val);
    saveSettings({ chat_noti_enabled: notificationOn ? 1 : 0, dnd_enabled: 1, dnd_from: val, dnd_to: dndTo });
  }, [notificationOn, dndTo, saveSettings]);

  const handleDndToChange = useCallback((val) => {
    setDndTo(val);
    saveSettings({ chat_noti_enabled: notificationOn ? 1 : 0, dnd_enabled: 1, dnd_from: dndFrom, dnd_to: val });
  }, [notificationOn, dndFrom, saveSettings]);

  const items = useMemo(() => [
    {
      id: "blocked",
      icon: "personIcon",
      label: "채팅방 사용자 관리",
      onClick: () => onOpenBlockedUsers?.(),
    },
    { id: "div1", type: "divider" },
    {
      id: "notification",
      icon: "alarm",
      label: "채팅방 전체 알림",
      type: "toggle",
      value: notificationOn,
      onToggle: handleNotificationToggle,
    },
    { id: "div2", type: "divider" },
    {
      id: "dnd",
      icon: "dnd",
      label: "방해금지 모드",
      type: "toggle",
      value: dndOn,
      onToggle: handleDndToggle,
      children: (
        <div className={`${settingsListStyles.dndTimeWrap} ${!dndOn ? settingsListStyles.dndTimeDisabled : ""}`}>
          <div className={settingsListStyles.dndTimeInner}>
            <p className={settingsListStyles.dndTimeLabel}>방해금지 시간 (24:00)</p>
            <div className={settingsListStyles.dndTimeRow}>
              <TimeDropdown value={dndFrom} onChange={handleDndFromChange} disabledTime={dndTo} />
              <span className={settingsListStyles.dndTimeSep}>~</span>
              <TimeDropdown value={dndTo} onChange={handleDndToChange} disabledTime={dndFrom} />
            </div>
          </div>
          {dndOn && <p className={settingsListStyles.dndTimeHint}>{formatTimeHint(dndFrom, dndTo)}</p>}
        </div>
      ),
    },
  ], [onOpenBlockedUsers, notificationOn, handleNotificationToggle, dndOn, handleDndToggle, dndFrom, dndTo, handleDndFromChange, handleDndToChange]);

  const activeToast = settingToast || toastMessage;

  return (
    <div className={`${styles.root} ${className}`.trim()}>
      <div className={styles.panel}>
        <BackHeader title={title} type="main" onBackClick={onBackClick} />
        <div className={styles.divider} />
        <div className={styles.content}>
          {loaded && <SettingsList items={items} />}
        </div>
      </div>
      <Toast message={activeToast} />
    </div>
  );
}

export default ChatSettingsPage;
